import { useEffect, useMemo, useReducer, useRef } from 'react';
import { canRedo, canUndo, commit, createHistory, redo, undo } from '../model/history';
import {
  addLayer, addPages, alignLayer, arrangeLayer, duplicateLayer, duplicatePage, findLayer, movePage, pageIndex, pageOf, pasteLayers, removeLayers, removePage, reorderLayer,
  setAspectRatio, setPageBackground, updateLayer, updateLayers,
} from '../model/operations';
import { framingOf } from '../model/framing';

// The editor's controller: the document under undo/redo plus the session around it
// (the page on the stage, selection, the text being edited, the photo being moved inside
// its mask, the open panel, zoom). The host owns the document:
// a new `value` from outside (an AI revision, a reloaded version) becomes an undoable step,
// and every local step is handed back through `onChange`. The host echoing a document it
// was given is recognised and ignored, so the two never loop.
const keep = (ids, doc) => (doc ? ids.filter(id => findLayer(doc, id)) : []);
const initial = value => ({ history: createHistory(value || null), origin: 'external', selection: [], editing: null, cropping: null, tool: null, toolMode: null, zoom: 'fit',
  pageId: value?.pages[0]?.id || null });
// The move mode lasts while its layer exists and still frames a photo.
const keepCropping = (id, doc) => (id && doc && framingOf(findLayer(doc, id)?.layer) ? id : null);
// The page on the stage survives a change; when the change took it away (an undone "add
// page", another version), the page now at its place takes over.
const keepPage = (pageId, from, to) => {
  if (!to) return null;
  if (to.pages.some(page => page.id === pageId)) return pageId;
  const index = from ? pageIndex(from, pageId) : 0;
  return to.pages[Math.min(index, to.pages.length - 1)].id;
};

function reducer(state, action) {
  const present = state.history.present;
  switch (action.type) {
    case 'external': {
      if (!action.document) return initial(null);
      if (!present) return { ...initial(action.document), tool: state.tool, zoom: state.zoom };
      return { ...state, history: commit(state.history, action.document, { label: action.label || 'external' }), origin: 'external',
        selection: keep(state.selection, action.document), editing: null, cropping: null, pageId: keepPage(state.pageId, present, action.document) };
    }
    case 'apply': {
      if (!present) return state;
      const result = action.fn(present, state);
      const next = result && result.doc !== undefined ? result.doc : result;
      if (!next || next === present) return state;
      // A page action may move the stage to another page (the one it added, the neighbour of the one it removed).
      const pageId = keepPage(result?.page || state.pageId, present, next);
      const turned = pageId !== state.pageId;
      const selection = result && result.select ? result.select : turned ? [] : keep(state.selection, next);
      return { ...state, history: commit(state.history, next, { key: action.key || null, label: action.label || null }), origin: 'local', selection, pageId,
        editing: !turned && state.editing && findLayer(next, state.editing) ? state.editing : null, cropping: turned ? null : keepCropping(state.cropping, next) };
    }
    case 'undo':
    case 'redo': {
      const history = action.type === 'undo' ? undo(state.history) : redo(state.history);
      if (history === state.history) return state;
      return { ...state, history, origin: 'local', selection: keep(state.selection, history.present), editing: null, cropping: keepCropping(state.cropping, history.present),
        pageId: keepPage(state.pageId, present, history.present) };
    }
    // Another page on the stage: what was selected or being edited there is let go.
    case 'page': {
      if (!present || action.id === state.pageId || !present.pages.some(page => page.id === action.id)) return state;
      return { ...state, pageId: action.id, selection: [], editing: null, cropping: null };
    }
    case 'select': return { ...state, selection: keep(action.ids, present), editing: action.ids.includes(state.editing) ? state.editing : null,
      cropping: action.ids.includes(state.cropping) ? state.cropping : null };
    case 'edit': return { ...state, editing: action.id, cropping: null, selection: action.id ? [action.id] : state.selection };
    // Moving a photo inside its mask (an image's frame, a shape, a text's letters).
    case 'crop': {
      const id = keepCropping(action.id, present);
      return id ? { ...state, cropping: id, selection: [id], editing: null, tool: null, toolMode: null } : { ...state, cropping: null };
    }
    case 'tool': {
      const tool = action.tool === state.tool && !action.mode && !action.force ? null : action.tool;
      return { ...state, tool, toolMode: tool ? action.mode || null : null };
    }
    case 'zoom': return { ...state, zoom: action.zoom };
    default: return state;
  }
}

// Copied layers outlive the editor that copied them (paste into another project).
let clipboard = [];

export default function useArtEditor({ value, onChange, onSelectionChange, readOnly = false }) {
  const [state, dispatch] = useReducer(reducer, value, initial);
  const present = state.history.present;
  const emitted = useRef(value);
  const latest = useRef({ onChange, onSelectionChange, readOnly, state });
  latest.current = { onChange, onSelectionChange, readOnly, state };

  useEffect(() => {
    if (value === emitted.current || value === present) return;
    emitted.current = value;
    dispatch({ type: 'external', document: value });
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state.origin !== 'local' || !present || present === emitted.current) return;
    emitted.current = present;
    latest.current.onChange?.(present, { label: state.history.label });
  }, [present, state.origin, state.history.label]);

  const page = present ? pageOf(present, state.pageId) : null;
  const selected = useMemo(() => (page ? state.selection.map(id => page.layers.find(layer => layer.id === id)).filter(Boolean) : []), [page, state.selection]);
  useEffect(() => { latest.current.onSelectionChange?.({ pageId: page?.id || null, layers: selected }); }, [selected, page?.id]);

  const actions = useMemo(() => {
    const writable = () => !latest.current.readOnly;
    const apply = (fn, meta = {}) => { if (writable()) dispatch({ type: 'apply', fn, ...meta }); };
    const current = () => latest.current.state;
    const pageId = () => current().pageId;
    return {
      apply,
      update: (id, patch, meta = {}) => apply(doc => updateLayer(doc, id, patch), { key: meta.key === undefined ? `${id}:${Object.keys(patch).join(',')}` : meta.key, label: meta.label }),
      updateMany: (ids, patch, meta = {}) => apply(doc => updateLayers(doc, ids, patch), meta),
      add: (layer, { index = null, select = true, edit = false } = {}) => {
        apply(doc => ({ doc: addLayer(doc, pageId(), layer, index), select: select ? [layer.id] : undefined }), { label: 'add' });
        if (edit && writable()) dispatch({ type: 'edit', id: layer.id });
      },
      remove: ids => apply(doc => removeLayers(doc, ids), { label: 'remove' }),
      duplicate: id => apply(doc => { const result = duplicateLayer(doc, id); return { doc: result.doc, select: result.id ? [result.id] : undefined }; }, { label: 'duplicate' }),
      reorder: (id, index) => apply(doc => reorderLayer(doc, id, index), { label: 'reorder' }),
      arrange: (id, where) => apply(doc => arrangeLayer(doc, id, where), { label: 'arrange' }),
      align: (id, edge) => apply(doc => alignLayer(doc, id, edge), { label: 'align' }),
      background: (fill, meta = {}) => apply(doc => setPageBackground(doc, pageId(), fill), { key: meta.key || 'page:background', label: 'background' }),
      aspectRatio: ratio => apply(doc => setAspectRatio(doc, ratio), { label: 'ratio' }),
      copy: ids => { const doc = current().history.present; clipboard = ids.map(id => doc && findLayer(doc, id)?.layer).filter(Boolean); return clipboard.length > 0; },
      paste: () => { if (clipboard.length) apply(doc => { const result = pasteLayers(doc, pageId(), clipboard); return { doc: result.doc, select: result.ids }; }, { label: 'paste' }); },
      hasClipboard: () => clipboard.length > 0,
      undo: () => { if (writable()) dispatch({ type: 'undo' }); },
      redo: () => { if (writable()) dispatch({ type: 'redo' }); },
      select: ids => dispatch({ type: 'select', ids: [].concat(ids || []) }),
      edit: id => { if (writable() || !id) dispatch({ type: 'edit', id }); },
      crop: id => { if (writable() || !id) dispatch({ type: 'crop', id }); },
      setTool: (tool, options = {}) => dispatch({ type: 'tool', tool, ...options }),
      setZoom: zoom => dispatch({ type: 'zoom', zoom }),
      // Pages: the stage follows the page just added or duplicated, and the neighbour of a removed one.
      setPage: id => dispatch({ type: 'page', id }),
      stepPage: delta => {
        const { state: now } = latest.current;
        const doc = now.history.present;
        if (!doc) return;
        const index = pageIndex(doc, now.pageId) + delta;
        if (index >= 0 && index < doc.pages.length) dispatch({ type: 'page', id: doc.pages[index].id });
      },
      addPages: (pages, index) => apply(doc => {
        const next = addPages(doc, pages, index);
        return { doc: next, page: next === doc ? undefined : pages[0].id, select: [] };
      }, { label: 'page' }),
      duplicatePage: id => apply(doc => { const result = duplicatePage(doc, id); return { doc: result.doc, page: result.id || undefined, select: [] }; }, { label: 'page' }),
      // Removing the page on the stage moves the stage to its neighbour; removing another one leaves it.
      removePage: id => apply((doc, state) => {
        const index = pageIndex(doc, id);
        const next = removePage(doc, id);
        const away = next !== doc && id === state.pageId;
        return { doc: next, page: away ? next.pages[Math.min(index, next.pages.length - 1)].id : undefined };
      }, { label: 'page' }),
      movePage: (id, index) => apply(doc => movePage(doc, id, index), { label: 'page' }),
    };
  }, []);

  return {
    document: present,
    page,
    pageIndex: present && page ? pageIndex(present, page.id) : 0,
    selection: state.selection,
    selected,
    editing: state.editing,
    cropping: state.cropping,
    tool: state.tool,
    toolMode: state.toolMode,
    zoom: state.zoom,
    canUndo: !readOnly && canUndo(state.history),
    canRedo: !readOnly && canRedo(state.history),
    lastLabel: state.history.label,
    ...actions,
  };
}
